/**
 * useEmailVariables Hook
 * 
 * React hook for managing email quick tags and variable resolution.
 * Wraps the core emailVariables utilities with React memoization.
 * 
 * Usage:
 *   const { tags, resolveVariables } = useEmailVariables({
 *     athlete: athleteProfile,
 *     coach: selectedCoach,
 *     context: { hasCoach: true, hasYouTube: !!athlete.youtube_channel_url, isThreadReply: false }
 *   });
 */

import { useMemo, useCallback } from 'react';
import { 
  getQuickTags, 
  resolveEmailVariables,
  QuickTag,
  EmailVariableContext,
  EventScheduleGame,
  ResolveData,
  StaffData
} from '../utils/emailVariables';

interface UseEmailVariablesProps {
  athlete?: any;
  coach?: any;
  sportInfo?: any;
  academicData?: any;
  performanceMetrics?: any;
  schoolInfo?: any;
  staffData?: StaffData | null;
  profileShareUrl?: string;
  /**
   * KGH-795 item 11. The athlete's games at the event this composer was opened
   * about — supplied by that surface only, so the {{athlete.tournamentSchedule}}
   * tag is offered and resolved exactly where it means something.
   */
  eventSchedule?: EventScheduleGame[] | null;
  context: {
    hasCoach: boolean;
    hasYouTube: boolean;
    isThreadReply: boolean;
    showAllTags?: boolean;
  };
}

interface UseEmailVariablesReturn {
  tags: QuickTag[];
  resolveVariables: (text: string) => string;
}

/**
 * Hook for managing email variables across all modals
 */
export const useEmailVariables = ({
  athlete,
  coach,
  sportInfo,
  academicData,
  performanceMetrics,
  schoolInfo,
  staffData,
  profileShareUrl,
  eventSchedule,
  context
}: UseEmailVariablesProps): UseEmailVariablesReturn => {

  // Generate quick tags based on context
  // Memoized to avoid regenerating on every render
  const tags = useMemo(() => {
    const variableContext: EmailVariableContext = {
      hasCoach: context.hasCoach,
      hasYouTube: context.hasYouTube,
      isThreadReply: context.isThreadReply,
      showAllTags: context.showAllTags,
      athlete,
      coach,
      sportInfo,
      academicData,
      performanceMetrics,
      schoolInfo,
      staffData,
      profileShareUrl,
      eventSchedule
    };

    return getQuickTags(variableContext);
  }, [athlete, coach, sportInfo, academicData, performanceMetrics, schoolInfo, staffData, profileShareUrl, eventSchedule, context.hasCoach, context.hasYouTube, context.isThreadReply, context.showAllTags]);

  // Create resolver function
  // Memoized to avoid recreating function on every render
  const resolveVariables = useCallback((text: string): string => {
    const resolveData: ResolveData = {
      athlete,
      coach,
      sportInfo,
      academicData,
      performanceMetrics,
      schoolInfo,
      staffData,
      profileShareUrl,
      eventSchedule
    };

    return resolveEmailVariables(text, resolveData);
  }, [athlete, coach, sportInfo, academicData, performanceMetrics, schoolInfo, staffData, profileShareUrl, eventSchedule]);

  return {
    tags,
    resolveVariables
  };
};
